'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="How to Check Cosmetic Ingredients for Microplastics: 27 Names to Avoid (2026 Guide)"
      author="Nora Vale"
      date="June 25, 2026"
      readTime="12 min read"
      heroImage="/blog/heroes/cosmetics.webp"
      heroImageAlt="How to check cosmetic ingredients for microplastics. 27 names to avoid 2026 reference guide"
    >
      <BlogLastUpdated iso="2026-06-25" formatted="June 25, 2026" />

      <BlogTLDR>
        Cosmetic microplastic ingredients fall into <strong>five
        families</strong>: <strong>(1) polyethylene + polypropylene
        polymers, (2) PEG-* compounds, (3) carbomer + acrylates polymers,
        (4) silicones (dimethicone, cyclopentasiloxane), (5) nylon and
        polyurethane</strong>. The 27 specific ingredient names to flag
        on any label are listed in the table below. If you see 2+ on
        a single product, it&apos;s a candidate to replace. The fastest
        way to check your existing products is the{' '}
        <Link href="/microplastic-ingredient-checker" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          free MicroPlastics ingredient checker tool
        </Link>{' '}, paste any ingredient list and get an instant audit. The
        MicroPlastics iOS app does the same thing from a barcode scan.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>27 specific ingredient names cover &gt;95% of microplastic ingredients in cosmetics. Learning to spot 5 of them (polyethylene, PEG, carbomer, acrylates copolymer, dimethicone) catches the majority.</>,
          <>EU REACH restricted intentionally added microplastics in rinse-off products from October 2023 and is phasing in restrictions for leave-on products through 2029. US still allows liquid polymers in leave-on products.</>,
          <>The free MicroPlastics ingredient checker at /microplastic-ingredient-checker parses any ingredient list automatically, paste from product page or app barcode scan.</>,
          <>Most products contain at least one of the 27 ingredients; you&apos;re looking for the worst offenders (3+ flag ingredients) to replace, not perfection.</>,
          <>Once you know the 5-7 ingredient name patterns, scanning a label takes 30 seconds and replaces years of brand-by-brand research.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Why the ingredient list is the only label that matters
      </h2>
      <p>
        Cosmetic marketing in 2026 has converged on a small list of
        claims that mean little: &ldquo;clean,&rdquo; &ldquo;natural,&rdquo;
        &ldquo;non-toxic,&rdquo; &ldquo;dermatologist-tested,&rdquo;
        &ldquo;reef-safe,&rdquo; &ldquo;cruelty-free.&rdquo; None of
        these claims are regulated or imply anything about microplastic
        content. A product can be <strong>cruelty-free, clean, and
        non-toxic</strong> while containing 6 different microplastic
        ingredients.
      </p>
      <p>
        The only label that tells you the truth is the{' '}
        <strong>ingredient list (INCI list)</strong>, required by FDA
        and EU regulation to list every ingredient in descending order
        of concentration. Learning to read this list catches the
        microplastic ingredients no marketing claim will mention.
      </p>

      <BlogAppCTA
        headline="Paste any ingredient list and check it now"
        sub="The free MicroPlastics ingredient checker parses any cosmetic ingredient list and flags every microplastic ingredient automatically. No signup. No download required (web tool). The iOS app does the same thing from a barcode scan."
        buttonLabel="Open the ingredient checker"
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The 27 microplastic ingredients to flag (grouped by family)
      </h2>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Family 1: Polyethylene and polypropylene polymers (6 names)</h3>
      <p>
        Direct microplastic polymers used as exfoliating beads,
        thickeners, and film-formers. The EU REACH 2023 restriction
        specifically targeted these in rinse-off products. US banned
        polyethylene microbeads in rinse-off cosmetics in 2017
        (Microbead-Free Waters Act) but they&apos;re still allowed in
        leave-on products.
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Polyethylene</strong>, most common. Used in scrubs, toothpaste, mascara.</li>
        <li><strong>Polypropylene</strong>, less common but used in scrubs and some cosmetic powders.</li>
        <li><strong>Polyethylene terephthalate (PET)</strong>, glitter, shimmer pigments in eyeshadow.</li>
        <li><strong>Polymethyl methacrylate (PMMA)</strong>, &ldquo;velvet feel&rdquo; powder cosmetics, blurring primers.</li>
        <li><strong>Polylactic acid (PLA)</strong>, newer &ldquo;biodegradable&rdquo; alternative; still a microplastic at typical use timescales.</li>
        <li><strong>Polystyrene</strong>, occasional thickener; rare in modern formulations.</li>
      </ul>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Family 2: PEG and PEG-derivatives (6 names)</h3>
      <p>
        Polyethylene glycol compounds, synthetic petrochemical polymers
        used as emulsifiers, humectants, and surfactant enhancers.
        Number after PEG indicates approximate molecular weight (higher
        = more polymer-like). EU REACH 2023 restricted some specific
        PEG-* compounds in rinse-off products.
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>PEG-* (any number from PEG-4 to PEG-200)</strong>, base PEG compounds. PEG-8, PEG-40, PEG-100 most common.</li>
        <li><strong>PEG-* stearate / palmitate / cocoate</strong>, fatty acid esters of PEG. Used as emulsifiers.</li>
        <li><strong>PEG-12 dimethicone, PEG-8 dimethicone</strong>, silicone-PEG hybrid surfactants. Worst-of-both-worlds polymer.</li>
        <li><strong>Polyethylene glycol</strong>, base compound, written out.</li>
        <li><strong>PPG-*</strong> (polypropylene glycol), close cousin of PEG, similar concerns.</li>
        <li><strong>Polyglyceryl-* esters</strong>, newer &ldquo;cleaner-sounding&rdquo; PEG alternatives; still polymer-derived.</li>
      </ul>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Family 3: Carbomer and acrylate polymers (5 names)</h3>
      <p>
        Polymer thickeners and film-formers used in gels, creams,
        sunscreens, and styling products. The &ldquo;copolymer&rdquo;
        suffix is the giveaway, anything ending in copolymer or
        crosspolymer is a polymer.
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Carbomer</strong>, most common polymer thickener; in face creams, hand sanitiser, sunscreen.</li>
        <li><strong>Acrylates copolymer / acrylates crosspolymer</strong>, film-former; in sunscreen, makeup, antiperspirant, styling products.</li>
        <li><strong>Acrylates/C10-30 alkyl acrylate crosspolymer</strong>, long-chain acrylate; in lotions and face moisturisers.</li>
        <li><strong>Acrylates/octylacrylamide copolymer</strong>, film-former in long-wear cosmetics and antiperspirant.</li>
        <li><strong>VP/eicosene copolymer, VP/VA copolymer</strong>, vinyl polymer film-formers for water-resistant cosmetics.</li>
      </ul>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Family 4: Silicones (6 names)</h3>
      <p>
        Silicone polymers are chemically liquid plastics, long chains
        of silicon-oxygen bonds with organic side groups. Common in
        conditioners, lotions, sunscreens, and primers for the smooth-
        skin / smooth-hair film. The -methicone or -siloxane suffix
        identifies them.
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Dimethicone</strong>, most common silicone polymer; in conditioner, lotion, sunscreen, deodorant, foundation.</li>
        <li><strong>Cyclopentasiloxane, cyclomethicone</strong>, volatile silicone carriers; evaporate but leave behind non-volatile silicone film.</li>
        <li><strong>Amodimethicone</strong>, amine-modified silicone; in conditioners specifically for cuticle smoothing.</li>
        <li><strong>Polysilicone-11, polysilicone-15</strong>, film-forming silicones in sunscreen.</li>
        <li><strong>Trimethylsiloxysilicate</strong>, long-wear silicone in liquid lipsticks and primers.</li>
        <li><strong>Dimethicone crosspolymer</strong>, silicone elastomer; in blurring primers and matte foundations.</li>
      </ul>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Family 5: Nylon, polyurethane, and other miscellaneous polymers (4 names)</h3>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Nylon-12, Nylon-6</strong>, synthetic fibre polymers; in long-wear mascara, lipstick, blurring primers.</li>
        <li><strong>Polyurethane-* (any number)</strong>, film-former in long-wear cosmetics and water-resistant mascara.</li>
        <li><strong>Polyquaternium-7, Polyquaternium-10</strong>, polymer conditioners; in shampoo, body wash, hand soap.</li>
        <li><strong>Sodium polyacrylate, sodium polyacrylate starch</strong>, superabsorbent polymer; in some body washes and gel cleansers.</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The complete 27-ingredient reference table
      </h2>

      <BlogComparisonTable
        caption="The 27 microplastic ingredients in cosmetics, what each does and where it&apos;s commonly found"
        columns={[
          { key: 'family', label: 'Family' },
          { key: 'ingredient', label: 'Ingredient' },
          { key: 'function', label: 'What it does' },
          { key: 'common', label: 'Most common in' },
        ]}
        rows={[
          { family: 'Polyethylene', ingredient: 'Polyethylene', function: 'Exfoliating beads, thickener', common: 'Toothpaste, scrubs, mascara' },
          { family: 'Polyethylene', ingredient: 'Polypropylene', function: 'Powder filler, scrub bead', common: 'Powder cosmetics, scrubs' },
          { family: 'Polyethylene', ingredient: 'Polyethylene terephthalate (PET)', function: 'Glitter, shimmer pigment', common: 'Eyeshadow, nail polish' },
          { family: 'Polyethylene', ingredient: 'Polymethyl methacrylate (PMMA)', function: 'Velvet-feel powder', common: 'Blurring primers, powder foundation' },
          { family: 'Polyethylene', ingredient: 'Polylactic acid (PLA)', function: 'Biodegradable polymer alternative', common: 'Newer &ldquo;clean&rdquo; powder cosmetics' },
          { family: 'Polyethylene', ingredient: 'Polystyrene', function: 'Thickener', common: 'Rare in modern formulations' },
          { family: 'PEG', ingredient: 'PEG-* (PEG-8, PEG-40, PEG-100, etc.)', function: 'Emulsifier, humectant', common: 'Lotion, body wash, deodorant, sunscreen' },
          { family: 'PEG', ingredient: 'PEG-* stearate / palmitate / cocoate', function: 'Fatty-acid emulsifier', common: 'Lotion, conditioner, body wash' },
          { family: 'PEG', ingredient: 'PEG-12 dimethicone, PEG-8 dimethicone', function: 'Silicone-PEG hybrid', common: 'Deodorant, conditioner, foundation' },
          { family: 'PEG', ingredient: 'Polyethylene glycol', function: 'Base PEG compound', common: 'Toothpaste, mouthwash' },
          { family: 'PEG', ingredient: 'PPG-* (polypropylene glycol)', function: 'PEG cousin', common: 'Antiperspirant, conditioner' },
          { family: 'PEG', ingredient: 'Polyglyceryl-* esters', function: 'PEG alternative', common: 'Newer &ldquo;clean&rdquo; cosmetic emulsifiers' },
          { family: 'Acrylate', ingredient: 'Carbomer', function: 'Gel thickener', common: 'Face creams, hand sanitiser, sunscreen' },
          { family: 'Acrylate', ingredient: 'Acrylates copolymer / crosspolymer', function: 'Film-former', common: 'Sunscreen, antiperspirant, hair styling' },
          { family: 'Acrylate', ingredient: 'Acrylates/C10-30 alkyl acrylate crosspolymer', function: 'Long-chain acrylate thickener', common: 'Lotions, face moisturisers' },
          { family: 'Acrylate', ingredient: 'Acrylates/octylacrylamide copolymer', function: 'Film-former, long-wear', common: 'Antiperspirant, makeup' },
          { family: 'Acrylate', ingredient: 'VP/eicosene copolymer, VP/VA copolymer', function: 'Vinyl film-former', common: 'Water-resistant sunscreen and cosmetics' },
          { family: 'Silicone', ingredient: 'Dimethicone', function: 'Smooth-skin film', common: 'Conditioner, lotion, sunscreen, deodorant, foundation' },
          { family: 'Silicone', ingredient: 'Cyclopentasiloxane, cyclomethicone', function: 'Volatile silicone carrier', common: 'Foundation, primer, deodorant' },
          { family: 'Silicone', ingredient: 'Amodimethicone', function: 'Cuticle-smoothing silicone', common: 'Conditioner, hair masks' },
          { family: 'Silicone', ingredient: 'Polysilicone-11, polysilicone-15', function: 'Film-forming silicone', common: 'Sunscreen, foundation' },
          { family: 'Silicone', ingredient: 'Trimethylsiloxysilicate', function: 'Long-wear silicone', common: 'Liquid lipstick, primer' },
          { family: 'Silicone', ingredient: 'Dimethicone crosspolymer', function: 'Silicone elastomer', common: 'Blurring primer, matte foundation' },
          { family: 'Nylon', ingredient: 'Nylon-12, Nylon-6', function: 'Synthetic fibre polymer', common: 'Mascara, lipstick, blurring primer' },
          { family: 'Nylon', ingredient: 'Polyurethane-*', function: 'Long-wear film-former', common: 'Long-wear cosmetics, waterproof mascara' },
          { family: 'Nylon', ingredient: 'Polyquaternium-7, Polyquaternium-10', function: 'Polymer conditioner', common: 'Shampoo, body wash, hand soap' },
          { family: 'Nylon', ingredient: 'Sodium polyacrylate', function: 'Superabsorbent polymer', common: 'Body wash, gel cleansers' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The 30-second label-reading workflow
      </h2>
      <ol className="list-decimal pl-6 space-y-2">
        <li><strong>Find the ingredient list</strong> (called the &ldquo;INCI list&rdquo;) on the back of the package, usually in small print.</li>
        <li><strong>Scan for the 5 dominant patterns:</strong> &ldquo;polyethylene&rdquo;, &ldquo;PEG-&rdquo; (any number after), &ldquo;carbomer&rdquo;, &ldquo;acrylates&rdquo; (anything with this word), &ldquo;dimethicone&rdquo; (or any ingredient ending in -methicone or -siloxane).</li>
        <li><strong>Count the flags.</strong> 0 flags = clean. 1-2 flags = moderate. 3+ flags = replace at next repurchase.</li>
        <li><strong>If unsure</strong>, paste the ingredient list into the{' '}
          <Link href="/microplastic-ingredient-checker" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
            free MicroPlastics ingredient checker
          </Link>{' '}, it parses the full 27-ingredient list automatically.
        </li>
        <li><strong>For a barcode-based audit of products you already own</strong>, the MicroPlastics iOS app does the same parsing from a barcode scan with one tap.</li>
      </ol>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What the EU REACH 2023 restriction covers (and doesn&apos;t)
      </h2>
      <p>
        The European Chemicals Agency (ECHA) restricted intentionally
        added microplastics in consumer products under REACH Annex XVII
        starting October 17, 2023. Phased timeline:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>October 2023:</strong> Rinse-off cosmetics with microbeads banned (polyethylene, polypropylene exfoliating beads). Already-stocked product can be sold until inventory clears.</li>
        <li><strong>October 2027:</strong> Rinse-off cosmetics with liquid polymers (PEG-based, acrylate, silicone) restricted in EU markets.</li>
        <li><strong>October 2029:</strong> Leave-on cosmetics (sunscreen, lotion, foundation, mascara, deodorant) restrictions phase in.</li>
        <li><strong>Beyond 2029:</strong> Glitter, agricultural products, paints, detergents under separate timelines.</li>
      </ul>
      <p>
        The US has no equivalent broad restriction. The 2017
        Microbead-Free Waters Act banned solid polyethylene microbeads
        in rinse-off cosmetics but allows liquid polymers (PEG,
        carbomer, acrylates, dimethicone) in all product categories.
        California Prop 65 has separate consumer disclosure requirements
        that some manufacturers comply with proactively.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What &ldquo;clean,&rdquo; &ldquo;natural,&rdquo; &ldquo;non-toxic&rdquo; actually mean
      </h2>
      <p>
        None of these terms are regulated. A product can carry any or
        all of them while containing 5+ of the 27 microplastic
        ingredients above. Practical examples:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>&ldquo;Clean&rdquo;:</strong> typically means free of parabens, sulfates, phthalates, formaldehyde. Says nothing about polymers.</li>
        <li><strong>&ldquo;Natural&rdquo;:</strong> often means &ldquo;contains some plant ingredients.&rdquo; The remaining 80% of the formulation can be polymer-heavy.</li>
        <li><strong>&ldquo;Non-toxic&rdquo;:</strong> regulatory term used loosely; means below acute toxicity thresholds. Does not address chronic exposure or polymer content.</li>
        <li><strong>&ldquo;Dermatologist-tested&rdquo;:</strong> means a dermatologist was involved in safety testing; does not imply polymer-free.</li>
        <li><strong>&ldquo;Reef-safe&rdquo;:</strong> specifically means free of oxybenzone and octinoxate (chemicals harmful to coral). Says nothing about other polymers.</li>
      </ul>
      <p>
        The ingredient list is the only label that tells you the truth.
        Reading it takes 30 seconds once you know the patterns.
      </p>

      <BlogAppCTA
        headline="Try the ingredient checker on a product you own right now"
        sub="Type or paste any cosmetic ingredient list into the free MicroPlastics ingredient checker, it flags every microplastic ingredient automatically and gives you a 0-100 risk score. iOS app does the same from a barcode scan."
        buttonLabel="Use the ingredient checker"
      />

      <p>
        We applied exactly this method to four of the most talked-about beauty
        brands in the world, reading the ingredient lists they publish
        themselves:{' '}
        <Link href="/blog/microplastics-in-makeup-brands-audited" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          81 formulas from Fenty, Huda Beauty, rhode and Kylie Cosmetics audited
        </Link>
        . 24 of 81 contained a solid synthetic polymer particle. See also why{' '}
        <Link href="/blog/clean-beauty-microplastics-loophole" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          no &ldquo;clean beauty&rdquo; standard excludes plastic
        </Link>
        .
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Common cosmetic categories and typical ingredient counts
      </h2>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Shampoo:</strong> usually 1-3 flags (dimethicone, PEG, polyquaternium-7 most common).</li>
        <li><strong>Conditioner:</strong> typically 3-5 flags (dimethicone + amodimethicone + polyquaternium + cyclopentasiloxane).</li>
        <li><strong>Body wash:</strong> usually 2-4 flags (PEG, sodium polyacrylate, polyquaternium).</li>
        <li><strong>Sunscreen (chemical):</strong> 3-5 flags (acrylates copolymer, dimethicone, carbomer, polyethylene).</li>
        <li><strong>Sunscreen (mineral, clean brands):</strong> 0-2 flags (Thinkbaby, Blue Lizard, Badger often have 0).</li>
        <li><strong>Body lotion:</strong> 2-4 flags (carbomer, dimethicone, PEG variants).</li>
        <li><strong>Face moisturiser:</strong> 2-5 flags. Premium luxury brands often worst because &ldquo;silky feel&rdquo; = silicone-heavy.</li>
        <li><strong>Deodorant (mainstream antiperspirant):</strong> 3-4 flags (cyclopentasiloxane, dimethicone, PEG, acrylates).</li>
        <li><strong>Deodorant (clean natural brands):</strong> 0-2 flags (Each &amp; Every, Schmidt&apos;s often have 0).</li>
        <li><strong>Toothpaste:</strong> 1-3 flags (polyethylene historical, PEG, carbomer).</li>
        <li><strong>Mascara:</strong> 2-4 flags (polyurethane, nylon, dimethicone, acrylates).</li>
        <li><strong>Liquid lipstick:</strong> 3-5 flags (trimethylsiloxysilicate, polyurethane, dimethicone crosspolymer).</li>
        <li><strong>Primer/blurring product:</strong> 4-6 flags (dimethicone crosspolymer, PMMA, silicones).</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Practical workflow, what to do with the audit
      </h2>
      <ol className="list-decimal pl-6 space-y-2">
        <li><strong>Don&apos;t throw out current products.</strong> Use what you have until it runs out. Replace with cleaner alternative at repurchase.</li>
        <li><strong>Replace highest-frequency products first.</strong> Your shampoo + deodorant + body lotion + sunscreen are used daily and matter more than your once-a-month face mask.</li>
        <li><strong>Set up a one-line cleaner alternative for each category.</strong> Once you&apos;ve identified a clean shampoo / conditioner / sunscreen / deodorant, reorder the same brand each time. Don&apos;t re-research.</li>
        <li><strong>Re-audit annually.</strong> Brands reformulate. Native deodorant has been reformulated multiple times. An annual rescan with the app catches drift.</li>
        <li><strong>Save your favourite cleaner brands as &ldquo;quick reorder&rdquo;</strong> in your Amazon / Target / Sephora favourites for easy repurchase.</li>
      </ol>

      <p>
        See also{' '}
        <Link href="/blog/60-second-bathroom-microplastic-audit" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          60-second bathroom audit
        </Link>
        ,{' '}
        <Link href="/blog/best-sunscreens-without-microplastics-brands-ranked" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          best sunscreens without microplastics
        </Link>
        ,{' '}
        <Link href="/blog/microplastics-in-deodorant-brands-ranked" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          deodorant brands ranked
        </Link>
        ,{' '}
        <Link href="/blog/best-microplastic-free-toothpaste" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          best microplastic-free toothpaste
        </Link>
        ,{' '}
        <Link href="/blog/worst-microplastic-ingredients" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          worst microplastic ingredients to avoid (full guide)
        </Link>
        , and{' '}
        <Link href="/blog/microplastics-in-cosmetics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in cosmetics (full guide)
        </Link>
        .
      </p>

      <BlogWhatTheAppChecks
        items={[
          <>Full ingredient list parser, flags all 27 ingredients automatically from barcode or pasted INCI list.</>,
          <>0-100 microplastic risk score per product based on count + concentration position in the ingredient list.</>,
          <>The cleaner same-format alternative when your current product scores high.</>,
          <>EU REACH restriction status, flags products restricted in EU but still sold in US.</>,
          <>Annual re-scan history so you catch brand reformulations over time.</>,
        ]}
      />

      <BlogFAQ
        items={[
          {
            question: 'What are the most common microplastic ingredients in cosmetics?',
            answer:
              'The 5 dominant ones to flag on every label: polyethylene (or any &ldquo;poly&rdquo; prefix polymer), PEG-* (any number), carbomer, acrylates copolymer / crosspolymer, and dimethicone (or any -methicone / -siloxane). These 5 catch the majority of microplastic content across all cosmetic categories. The full list of 27 names covers &gt;95% of microplastic ingredients used in cosmetics.',
          },
          {
            question: 'How can I check if my cosmetics contain microplastics?',
            answer:
              'Three ways. (1) Read the ingredient list and look for the 5 patterns above. Takes 30 seconds with practice. (2) Paste the ingredient list into the free MicroPlastics ingredient checker tool at /microplastic-ingredient-checker, it parses all 27 ingredient families automatically. (3) Scan the product barcode with the MicroPlastics iOS app for a one-tap audit.',
          },
          {
            question: 'Is dimethicone a microplastic?',
            answer:
              'Dimethicone is a silicone polymer, chemically a liquid plastic. EU REACH 2023 restricts certain silicones from rinse-off products and is phasing in restrictions for leave-on products through 2029. From a microplastic exposure standpoint, dimethicone is bioaccumulative in marine environments and persistent on skin. The most common ingredient to flag in conditioners, lotions, sunscreens, and deodorants.',
          },
          {
            question: 'Are PEG ingredients safe in cosmetics?',
            answer:
              'PEG (polyethylene glycol) compounds are FDA-approved and not acutely toxic. The microplastic concern is the polymer chemistry. PEG-* compounds are synthetic petrochemical polymers that don&apos;t biodegrade. EU REACH 2023 restricts some PEG-* compounds in rinse-off products. PEG-12 dimethicone (silicone-PEG hybrid) is the most-flagged variant.',
          },
          {
            question: 'What is the EU REACH microplastic restriction?',
            answer:
              'In October 2023, the European Chemicals Agency restricted intentionally added microplastics in consumer products under REACH Annex XVII. Rinse-off cosmetics with microbeads were banned immediately. Rinse-off cosmetics with liquid polymers phase out by October 2027. Leave-on cosmetics (sunscreen, lotion, mascara) phase out by October 2029. The US has no equivalent broad restriction.',
          },
          {
            question: 'Do &ldquo;clean&rdquo; or &ldquo;natural&rdquo; cosmetic brands contain microplastics?',
            answer:
              'Often yes. &ldquo;Clean&rdquo; and &ldquo;natural&rdquo; are unregulated terms that typically mean free of parabens, sulfates, phthalates, and say nothing about polymers. Many natural brands (Native deodorant, Lume, some Tom&apos;s of Maine variants) contain dimethicone, PEG, or propylene glycol. Read the ingredient list separately from the marketing claim.',
          },
          {
            question: 'Is the MicroPlastics ingredient checker free?',
            answer:
              'Yes. The web ingredient checker at /microplastic-ingredient-checker is free with no signup. Paste any ingredient list and get an instant audit. The MicroPlastics iOS app is also free to start with 3 scans, enough to check your highest-risk products first.',
          },
          {
            question: 'How accurate is reading the ingredient list yourself?',
            answer:
              'For the 5 dominant patterns (polyethylene, PEG, carbomer, acrylates, dimethicone) reading the label catches the vast majority of microplastic ingredients. For the full 27-ingredient list including silicone variants (cyclopentasiloxane, polysilicone-11, trimethylsiloxysilicate), nylon, polyurethane, and polyquaternium, a tool catches what manual scanning misses. We recommend reading the label for quick judgement + the tool for comprehensive audit.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'European Chemicals Agency',
            year: 2023,
            title: 'Restriction of intentionally added microplastics: REACH Annex XVII (Commission Regulation 2023/2055)',
            publisher: 'ECHA',
            url: 'https://echa.europa.eu/hot-topics/microplastics',
          },
          {
            authors: 'US Food and Drug Administration',
            year: 2024,
            title: 'Microbead-Free Waters Act of 2015: implementation and ongoing cosmetic policy',
            publisher: 'FDA',
            url: 'https://www.fda.gov/cosmetics/cosmetics-laws-regulations/microbead-free-waters-act-faqs',
          },
          {
            authors: 'Environmental Working Group',
            year: 2025,
            title: 'EWG Skin Deep cosmetic ingredient database',
            publisher: 'EWG',
            url: 'https://www.ewg.org/skindeep/',
          },
          {
            authors: 'Cosmetic Ingredient Review Expert Panel',
            year: 2024,
            title: 'Safety assessment of polymer ingredients in cosmetics',
            publisher: 'CIR',
            url: 'https://www.cir-safety.org/',
          },
          {
            authors: 'Personal Care Products Council',
            year: 2024,
            title: 'INCI ingredient nomenclature standards',
            publisher: 'PCPC',
            url: 'https://www.personalcarecouncil.org/resources/inci/',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
